import { Button, Stack, Typography } from "@mui/material";
import {
  DropDownList,
  DropDownListChangeEvent,
  ListItemProps,
} from "@progress/kendo-react-dropdowns";
import axios, { AxiosError } from "axios";
import LoadingComponent from "components/LoadingComponent";
import { useAppDispatch } from "hooks/redux";
import { ReactElement, cloneElement, useState } from "react";
import { showApiErrorMessage } from "store/errorModalSlice";
import { setNewElements } from "store/rfiSlice";
import { RfiElementSelectionList } from "types/RfiTypes";
import RfiElement from "../../types/RfiElement";
import { USER_ALLOW_ALL } from "./ElementEditor";

export interface ElementCopierProps {
  element: RfiElement;
  modifyElement: any;
  rfiId?: any;
  handleCopy?: any;
  handleMove?: any;
  currentSection?: string;
  targetElementList: Array<RfiElementSelectionList>;
  userRole?: string;
  handleUpdate: (element?: RfiElement) => Promise<void>;
  modifyElements?: (modifyAction: () => Promise<void>) => Promise<void>;
}

const ElementCopier = (props: ElementCopierProps) => {
  // Constants
  const destinations = ["within this RFI", "to the Library"];
  const positions = ["before", "after"];

  // Hooks
  const dispatch = useAppDispatch();

  // State
  const [isModifying, setIsModifying] = useState(false);
  const [destination, setDestination] = useState<string>(destinations[0]);
  const [position, setPosition] = useState<string>(positions[1]);
  const [targetElement, setTargetElement] =
    useState<RfiElementSelectionList | null>(null);

  // UI Handlers
  const handleChangeDestination = (event: DropDownListChangeEvent) => {
    setDestination(event.target.value);
  };

  const handleChangePosition = (event: DropDownListChangeEvent) => {
    setPosition(event.target.value);
  };

  const handleChangeTarget = (event: DropDownListChangeEvent) => {
    setTargetElement(event.target.value);
  };

  // API Handler
  const handleCopy = async (): Promise<void> => {
    setIsModifying(true);
    await props.handleUpdate();

    let copyParams = {};

    if (destination === destinations[0]) {
      copyParams = {
        rfiId: props.rfiId ?? 0,
        sectionGroupName: props.currentSection,
        targetElementId: targetElement?.elementId ?? 0,
        position: position,
      };
    }

    await axios
      .post(`/api/v1/elements/rfi/${props.element.elementId}/copy`, copyParams)
      .then(async () => {
        await axios
          .get(`/api/v1/rfi/${props.rfiId}/elements/${props.currentSection}`)
          .then((resElements) => {
            dispatch(
              setNewElements({
                elements: resElements.data,
                section: props.currentSection ?? "",
              })
            );
            return resElements.data;
          })
          .catch((error: Error | AxiosError) => {
            console.log(error);
            dispatch(showApiErrorMessage({ errorResponse: error }));
          });
      })
      .catch((error: Error | AxiosError) => {
        console.log(error);
        dispatch(showApiErrorMessage({ errorResponse: error }));
      })
      .finally(() => {
        setIsModifying(false);
      });
  };

  // Differentiate current element
  const itemRender = (
    li: ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    //const index = itemProps.index;
    let itemChildren = (
      <span>
        <>{li.props.children}</>
      </span>
    );

    if (itemProps.dataItem.elementId === props.element.elementId) {
      itemChildren = (
        <span style={{ fontStyle: "bold" }}>
          <>
            {"(THIS ELEMENT) "}
            {li.props.children}
          </>
        </span>
      );
    }
    return cloneElement(li, li.props, itemChildren);
  };

  // Render
  return (
    <>
      {isModifying && <LoadingComponent />}
      <Stack direction="row" spacing={2} m={2}>
        <Typography>Copy this element</Typography>

        {/* Copy to Library (ADMIN ONLY) */}
        {props.userRole && props.userRole === USER_ALLOW_ALL && (
          <DropDownList
            id="copy-destination"
            data={destinations}
            value={destination}
            // disabled={user.hasLibraryWriteAccess === true}
            onChange={handleChangeDestination}
            style={{ width: "150px" }}
          />
        )}

        {/* Copy within this RFI Section */}
        {destination === destinations[0] && (
          <>
            <DropDownList
              data={positions}
              value={position}
              onChange={handleChangePosition}
              style={{ width: "100px" }}
            />

            <DropDownList
              data={props.targetElementList}
              textField="fieldLabel"
              itemRender={itemRender}
              value={targetElement}
              onChange={handleChangeTarget}
              style={{ width: "400px" }}
            />
          </>
        )}
      </Stack>
      <Button
        title="Copy Element"
        variant="contained"
        onClick={handleCopy}
        disabled={destination !== destinations[1] && !targetElement}
      >
        Copy
      </Button>
    </>
  );
};

export default ElementCopier;
